import React, { useState } from 'react';
import { ApiService } from '../../services/ApiService';
import { useTranslation } from '../../i18n.ts';

/**
 * MiniRegisterForm — sub-component van AcceptInvitePage (Refs #778, plan 4 B1).
 *
 * Gebruikt door uitgenodigden zonder Pecktree-account: e-mail komt uit invite
 * (read-only weergegeven), gebruiker kiest zelf naam + wachtwoord. Backend
 * `POST /api/auth/register` herkent het org-invite-token en zet `is_verified`
 * direct op TRUE — er hoeft geen verificatie-mail doorlopen te worden.
 *
 * Na succesvolle registratie loggen we expliciet in (auto-login is op moment
 * van schrijven niet gegarandeerd in de register-respons) en triggeren we
 * `onSuccess` zodat de parent de invite alsnog kan accepteren.
 */
interface Props {
  invitationToken: string;
  email: string;
  onSuccess: () => void;
}

export function MiniRegisterForm({ invitationToken, email, onSuccess }: Props) {
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t, lang } = useTranslation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await ApiService.register({
        email,
        password,
        firstName,
        lastName,
        invitationToken,
        language: lang,
      });

      // Bij sommige flows logt /register al in via cookie; bij anderen niet.
      // Doe expliciet login om zeker te zijn dat session-cookie aanwezig is
      // voor de daaropvolgende accept-call.
      await ApiService.login({ email, password });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ogRegistrationFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-sm text-pt-sage-text">
        {t('ogCreateAccountFor')} <strong>{email}</strong>:
      </p>
      <input
        type="email"
        value={email}
        readOnly
        aria-label={t('ogEmailFixedByInvite')}
        className="w-full px-3 py-2 border border-pt-cream rounded text-sm bg-pt-cream/40 text-pt-sage-text"
      />
      <input
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder={t('firstName')}
        aria-label={t('firstName')}
        aria-required="true"
        aria-invalid={error ? true : undefined}
        className="w-full px-3 py-2 border border-pt-cream rounded text-sm"
        required
      />
      <input
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        placeholder={t('lastName')}
        aria-label={t('lastName')}
        aria-required="true"
        aria-invalid={error ? true : undefined}
        className="w-full px-3 py-2 border border-pt-cream rounded text-sm"
        required
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t('ogPasswordMin8')}
        aria-label={t('password')}
        aria-required="true"
        aria-describedby="mini-register-password-hint"
        aria-invalid={error ? true : undefined}
        className="w-full px-3 py-2 border border-pt-cream rounded text-sm"
        required
        minLength={8}
      />
      <p id="mini-register-password-hint" className="text-xs text-pt-sage-text">
        {t('ogPasswordHint')}
      </p>
      {error && (
        <p role="alert" className="text-sm text-pt-ruby">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 bg-pt-forest text-pt-gold rounded font-semibold disabled:opacity-60"
      >
        {submitting ? t('creating') : t('ogCreateAccountAndAccept')}
      </button>
    </form>
  );
}

export default MiniRegisterForm;
